import { useState } from "react";
import {
  useDeleteRiderRequestMutation,
  useFetchRiderRequestsQuery,
} from "../store";
import Pagination from "../components/common/Pagination.tsx";
import { FaRegTrashAlt } from "react-icons/fa";
import TextField from "../components/common/TextField.tsx";
import useDebounce from "../hooks/useDebounce.tsx";
import { RiderRequest, TransportTypeLabels } from "../types";
import { showToast, ToastType } from "../utils/toast.utils.ts";
import Modal from "../components/common/Modal.tsx";
import Button from "../components/common/Button.tsx";
import { centeredTdClasses, tdClasses, thClasses } from "../configs";

const RiderApplications: React.FC = () => {
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [name, setName] = useState<string>("");
  const debouncedName = useDebounce(name, 400);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState<RiderRequest | null>(
    null
  );

  const [deleteRiderRequest] = useDeleteRiderRequestMutation();

  const confirmDelete = (requestToDelete: RiderRequest) => {
    setRequestToDelete(requestToDelete);
    setShowConfirmModal(true);
  };

  const handleDeleteRequest = async () => {
    if (!requestToDelete) return;
    const result = await deleteRiderRequest(requestToDelete._id);
    if (!result.error) {
      showToast("Demande supprimée", ToastType.SUCCESS);
    }
    setShowConfirmModal(false);
    setRequestToDelete(null);
  };

  const { data: requests } = useFetchRiderRequestsQuery({
    page,
    perPage,
    name: debouncedName,
  });

  return (
    <div className="w-full flex flex-col mx-auto p-4">
      <div className="w-1/4 mb-4">
        <TextField
          id="name"
          value={name}
          onChange={(e) => setName((e.target as HTMLInputElement).value)}
          placeholder="Nom ou prénom du livreur"
        />
      </div>
      <table
        className="table-fixed w-full rounded-xl"
        style={{ borderRadius: "50px" }}
      >
        <colgroup>
          <col className="w-2/12" />
          <col className="w-2/12" />
          <col className="w-3/12" />
          <col className="w-1/12" />
          <col className="w-1/12" />
          <col className="w-1/12" />
          <col className="w-1/12" />
          <col className="w-1/12" />
        </colgroup>
        <thead>
          <tr>
            <th
              key={0}
              className={thClasses}
            >
              Nom
            </th>
            <th
              key={1}
              className={thClasses}
            >
              Prénom
            </th>
            <th
              key={2}
              className={thClasses}
            >
              Email
            </th>
            <th
              key={3}
              className={thClasses}
            >
              Téléphone
            </th>
            <th
              key={4}
              className={thClasses}
            >
              Ville
            </th>
            <th
              key={5}
              className={thClasses}
            >
              Moyen de transport
            </th>
            <th
              key={6}
              className={thClasses}
            >
              Date de création
            </th>
            <th
              key={7}
              className={thClasses}
            >
              Supprimer
            </th>
          </tr>
        </thead>
        <tbody>
          {requests?.data?.map((row) => {
            return (
              <tr key={row._id}>
                <td className={tdClasses}> {row.lastName} </td>
                <td className={tdClasses}> {row.firstName} </td>
                <td className={tdClasses}> {row.email} </td>
                <td className={tdClasses}> {row.phone} </td>
                <td className={centeredTdClasses}> {row.city} </td>
                <td className={centeredTdClasses}>
                  {TransportTypeLabels[row.transportType]}
                </td>
                <td className={tdClasses}> {row.createdAt} </td>
                <td className={tdClasses}>
                  <div className="w-full flex justify-center">
                    <FaRegTrashAlt
                      className="w-8 text-primary cursor-pointer"
                      onClick={() => {
                        confirmDelete(row);
                      }}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Pagination
        paginationInfo={{
          page,
          perPage,
          count: requests?.paginationInfo.count || 1,
        }}
        setPage={setPage}
        setPerPage={setPerPage}
      />
      {showConfirmModal && (
        <Modal
          onClose={() => setShowConfirmModal(false)}
          actionBar={
            <>
              <Button
                className="bg-gray-300 px-4 py-2 rounded mr-2"
                onClick={() => setShowConfirmModal(false)}
              >
                Annuler
              </Button>
              <Button
                className="bg-primary text-white px-4 py-2 rounded"
                onClick={handleDeleteRequest}
              >
                Supprimer
              </Button>
            </>
          }
        >
          <div className="bg-white text-center font-semibold">
            Êtes-vous sûr de vouloir supprimer la demande de{" "}
            {`${requestToDelete?.firstName} ${requestToDelete?.lastName}`} ?
          </div>
        </Modal>
      )}
    </div>
  );
};

export default RiderApplications;
